import React, {Fragment} from 'react'
import {useI18n} from 'i18n/I18nContext'
import {
  CertificateTemplate,
  ShapeDecoration,
} from 'organization/Event/Certification/Builder/templates'

export function fillTemplate(
  str: string,
  variables: Record<string, any>,
): string {
  if (!str) return ''
  return str.replace(/{{(.*?)}}/g, (_, key) => {
    const path = key.trim().split('.')
    let val: any = variables
    for (const part of path) {
      if (val === undefined || val === null) return ''
      val = val[part]
    }
    return val === undefined || val === null ? '' : String(val)
  })
}

const MultilineText = ({
  text,
  x,
  y,
  fontFamily,
  fontSize = 16,
  fill = '#000',
  textAnchor = 'start',
  fontWeight,
  lineHeight = 1.4,
}: {
  text: string
  x: number
  y: number
  fontFamily?: string
  fontSize?: number
  fill?: string
  textAnchor?: 'start' | 'middle' | 'end'
  fontWeight?: number | string
  lineHeight?: number
}) => {
  if (!text) return null
  const lines = text.split('\n')
  return (
    <text
      x={x}
      y={y}
      textAnchor={textAnchor}
      fontFamily={fontFamily}
      fontSize={fontSize}
      fill={fill}
      fontWeight={fontWeight}
    >
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : fontSize * lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  )
}

function renderShape(
  shape: ShapeDecoration,
  layout: CertificateTemplate['layout'],
) {
  switch (shape.type) {
    case 'ribbon':
      return (
        <rect
          key={`ribbon-${shape.x}-${shape.y}`}
          x={shape.x}
          y={shape.y}
          width={shape.width}
          height={shape.height}
          rx={6}
          fill={layout.themeColor}
          opacity={0.95}
        />
      )

    case 'stripe':
      return (
        <rect
          key={`stripe-${shape.x}-${shape.y}`}
          x={shape.x}
          y={shape.y}
          width={shape.width}
          height={shape.height}
          transform={`rotate(${shape.rotate ?? 0})`}
          fill={layout.themeColor}
          opacity={0.12}
        />
      )

    case 'circle-decor':
      return (
        <Fragment key={`circles-${shape.cx}-${shape.cy}`}>
          <circle
            key={`circle-${shape.cx}-${shape.cy}`}
            cx={shape.cx}
            cy={shape.cy}
            r={shape.r}
            fill={layout.themeColor}
            opacity={0.12}
          />
          <circle
            key={`circle-${shape.cx}-${shape.cy}-2`}
            cx={1200 - shape.cx}
            cy={800 - shape.cy}
            r={shape.r}
            fill={layout.themeColor}
            opacity={0.12}
          />
        </Fragment>
      )

    default:
      return null
  }
}

export const CertificatePreview: React.FC<{
  template: CertificateTemplate
  variables: Record<string, any>
}> = ({template, variables}) => {
  const {t} = useI18n()
  const {layout, fields, decoration} = template

  const filled = Object.fromEntries(
    Object.entries(fields).map(([key, val]) => [
      key,
      fillTemplate(val, variables),
    ]),
  ) as Record<string, string>

  const titleY = decoration?.titleY ?? 120
  const recipientY = decoration?.recipientY ?? layout.height / 2 - 40
  const descriptionY = decoration?.descriptionY ?? layout.height / 2 + 20
  const inset = decoration?.border?.inset ?? 24
  const strokeWidth = decoration?.border?.strokeWidth ?? 6
  const dash = decoration?.border?.dash?.join(',') ?? undefined

  return (
    <svg
      id="certificate-svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      style={{width: '100%', height: 'auto', display: 'block'}}
    >
      <rect width="100%" height="100%" fill={layout.backgroundColor} rx={12} />

      {decoration?.shapes?.map((shape) => renderShape(shape, layout))}

      <rect
        x={inset / 2}
        y={inset / 2}
        width={layout.width - inset}
        height={layout.height - inset}
        fill="none"
        stroke={layout.themeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={dash}
        rx={12}
        opacity={0.95}
      />

      <MultilineText
        text={filled.title}
        x={layout.width / 2}
        y={titleY}
        textAnchor="middle"
        fontFamily={layout.fontFamily}
        fontSize={36}
        fontWeight={700}
        fill={layout.themeColor}
      />

      {filled.subtitle && (
        <MultilineText
          text={filled.subtitle}
          x={layout.width / 2}
          y={titleY + 100}
          textAnchor="middle"
          fontFamily={layout.fontFamily}
          fontSize={18}
          fill="#333"
        />
      )}

      <MultilineText
        text={filled.recipientName}
        x={layout.width / 2}
        y={recipientY}
        textAnchor="middle"
        fontFamily={layout.fontFamily}
        fontSize={48}
        fontWeight={700}
        fill="#111"
      />

      {filled.description && (
        <MultilineText
          text={filled.description}
          x={layout.width / 2}
          y={descriptionY}
          textAnchor="middle"
          fontFamily={layout.fontFamily}
          fontSize={18}
          fill="#555"
          lineHeight={1.4}
        />
      )}

      <MultilineText
        text={`${t('orgCert.previewIssuedBy')}\n${filled.issuer ?? ''}`}
        x={80}
        y={layout.height - 120}
        fontFamily={layout.fontFamily}
        fontSize={16}
        fill="#333"
      />
      <MultilineText
        text={filled.date}
        x={80}
        y={layout.height - 60}
        fontFamily={layout.fontFamily}
        fontSize={14}
        fill="#666"
      />

      {template.signature ? (
        <image
          href={template.signature}
          x={layout.width - 420}
          y={layout.height - 190}
          width={300}
          height={120}
          preserveAspectRatio="xMidYMid meet"
        />
      ) : (
        <rect
          x={layout.width - 420}
          y={layout.height - 190}
          width={300}
          height={120}
          fill="none"
          stroke="#ddd"
          strokeWidth={1}
          rx={6}
        />
      )}

      <text
        x={layout.width - 260}
        y={layout.height - 60}
        textAnchor="middle"
        fontSize={14}
        fill="#333"
      >
        {t('orgCert.previewSignature')}
      </text>
    </svg>
  )
}
